import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, Save, ArrowRight } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { Alert, Spinner, EmptyState, Badge } from '@/components/ui/misc';
import { Dashboard } from '@/components/dashboard/Dashboard';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { timeUntil } from '@/lib/utils';

export function AnonResults() {
  const { runId } = useParams<{ runId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['anon-run', runId],
    queryFn: () => api.getAnalysis(runId!),
    enabled: Boolean(runId),
  });

  async function claim() {
    if (!runId) return;
    setClaimError(null);
    setClaiming(true);
    try {
      const res = await api.claimAnalysis(runId);
      navigate(`/app/results/${res.run.runId}`);
    } catch (err) {
      setClaimError(err instanceof ApiError ? err.message : 'Could not save this report.');
    } finally {
      setClaiming(false);
    }
  }

  return (
    <div className="min-h-full">
      <header className="border-b border-border/70 bg-bg/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Visiward home">
            <Logo />
          </Link>
          <Link to="/analyze">
            <Button variant="secondary" size="sm">
              New analysis
            </Button>
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {isLoading && (
          <div className="flex justify-center py-24">
            <Spinner className="h-8 w-8" />
          </div>
        )}

        {error && (
          <EmptyState
            title="This result is unavailable"
            description="Anonymous results expire after 24 hours and can only be viewed on the device that created them. Run a new analysis to continue."
            action={
              <Link to="/analyze">
                <Button>Run a new analysis</Button>
              </Link>
            }
          />
        )}

        {data && (
          <>
            {/* Save banner */}
            <div className="mb-6 panel-raised flex flex-col items-start justify-between gap-4 p-5 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-blue/10 text-accent-blue">
                  <Save size={18} />
                </span>
                <div>
                  <p className="font-semibold text-text-primary">
                    Create a free account to save this report and track changes over time.
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-text-secondary">
                    <Clock size={13} /> This result expires in {timeUntil(data.run.expiresAt)}.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {user ? (
                  <Button onClick={claim} disabled={claiming}>
                    {claiming ? <Spinner /> : <Save size={16} />}
                    Save to my account
                  </Button>
                ) : (
                  <>
                    <Link to="/login" state={{ from: `/results/${runId}` }}>
                      <Button variant="secondary">Sign in</Button>
                    </Link>
                    <Link to="/register">
                      <Button>
                        Create account <ArrowRight size={16} />
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>

            {claimError && (
              <div className="mb-4">
                <Alert tone="error">{claimError}</Alert>
              </div>
            )}

            <div className="mb-6 flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-text-primary">
                {data.run.project.brandName ?? data.run.project.websiteUrl}
              </h1>
              <Badge tone="blue">{statusLabel(data.run.status)}</Badge>
            </div>

            <Dashboard run={data.run} />
          </>
        )}
      </div>
    </div>
  );
}

function statusLabel(status: string): string {
  return status === 'partially_completed' ? 'Partial results' : 'Completed';
}
